import template from './employee_view_employment_component.html';
import * as Permissions from '../../lib/permission_utils.js';
import * as Utils from '../../lib/savanna_utils.js';
import tr from '../../lib/default-translator.js';

class EmployeeViewEmploymentComponentViewModel
{
	constructor(bindings)
	{
		this.bindings = bindings;
		this.loader = ko.observable(false);
		this.employment_info = ko.observableArray([]);
		this.employee = ko.observable(null);
		this.employee_id = ko.observable();
		this.user_access_roles = ko.observableArray([]);
		this.salary_structure_types = ko.observableArray([]);
		this.companies = ko.observableArray([]);
		this.contract_types = ko.observableArray([]);
		this.resign_types = ko.observableArray([]);
		this.branches = ko.observableArray([]);
		this.pending_resignation = ko.observable(false);
		this.pending_resignation_schedule_id = ko.observable(null);

		this.logged_in_employee_id = Utils.get_local_storage_nullable('employee_id');
		this.lookupsPromise = null;
		this.loadPromise = null;
		this.loadPromiseEmployeeId = null;
		this.loadVersion = 0;

		this.resign_employee = this.resign_employee.bind(this);

		this.createDepartment = (config = {}) =>
		{
			const row = {
				array_pos: config.array_pos ?? '',
				active: ko.observable(config.active ?? true),
				company: ko.observable(config.company ?? ''),
				company_id: ko.observable(config.company_id ?? ''),
				date_ended: ko.observable(config.date_ended ?? ''),
				date_started: ko.observable(config.date_started ?? new Date().toJSON().slice(0, 10)),
				dates: ko.observable(config.dates ?? ''),
				department: ko.observable(config.department ?? ''),
				department_id: ko.observable(config.department_id ?? ''),
				departments: ko.observableArray([]),
				departments_loading: ko.observable(false),
				employee_department_id: ko.observable(config.employee_department_id ?? null),
				contract_type: ko.observable(config.contract_type ?? ''),
				employee_code: ko.observable(config.employee_code ?? ''),
				comment: ko.observable(config.comment ?? ''),
				resign_type: ko.observable(config.resign_type ?? ''),
				position: ko.observable(config.position ?? ''),
				salary_structure: ko.observable(config.salary_structure ?? ''),
				branch_id: ko.observable(config.branch_id ?? ''),
				branch: ko.observable(config.branch ?? '')
			};

			row.company_id.subscribe(companyId =>
			{
				this.loadDepartmentsForRow(row, companyId, true);
			});

			return row;
		};

		this.onEmployeeChanged = employee =>
		{
			if (!employee || !employee.employee_id)
				return;

			this.employee(employee);
			this.employee_id(employee.employee_id);
			this.loadPage();
		};

		if (bindings.employee && ko.isObservable(bindings.employee))
		{
			bindings.employee.subscribe(this.onEmployeeChanged);
			this.onEmployeeChanged(bindings.employee());
		}
		else if (bindings.employee)
			this.onEmployeeChanged(bindings.employee);
		else if (bindings.employee_id && ko.isObservable(bindings.employee_id))
		{
			this.employee_id(bindings.employee_id());
			bindings.employee_id.subscribe(id => this.employee_id(id));
		}
	}

	async loadLookups()
	{
		if (this.lookupsPromise)
			return this.lookupsPromise;

		this.lookupsPromise = Promise.all([
			Grape.fetches.getJSON('/api/record', {
				table: 'salary_structure', schema: 'public',
				sortfield: 'salary_structure', sortorder: 'asc'
			}),
			Grape.fetches.getJSON('/api/record', {
				table: 'company', schema: 'public',
				sortfield: 'company', sortorder: 'asc'
			}),
			Grape.cache.get('ContractTypeLookup'),
			Grape.fetches.getJSON('/api/record', {
				table: 'resign_types', schema: 'public', limit: 1000000,
				sortfield: 'sort_order', sortorder: 'asc'
			}),
			Grape.fetches.getJSON('/api/record', {
				table: 'branch', schema: 'public', limit: 1000000,
				sortfield: 'branch', sortorder: 'asc'
			})
		]).then(([salaryStructures, companies, contractTypes, resignTypes, branches]) =>
		{
			this.salary_structure_types(salaryStructures.records || []);
			this.companies(companies.records || []);
			this.contract_types(contractTypes || []);
			this.resign_types(resignTypes.records || []);
			this.branches(branches.records || []);
		}).catch(error =>
		{
			this.lookupsPromise = null;
			throw error;
		});

		return this.lookupsPromise;
	}

	async loadPage()
	{
		const employee = this.employee();
		if (!employee || !employee.employee_id)
			return;

		const employeeId = employee.employee_id;
		if (this.loadPromise && this.loadPromiseEmployeeId === employeeId)
			return this.loadPromise;

		const version = ++this.loadVersion;
		this.loadPromiseEmployeeId = employeeId;
		this.loader(true);

		this.loadPromise = (async () =>
		{
			try
			{
				const [employmentResponse, schedulesResponse, rolesResponse] = await Promise.all([
					Grape.fetches.getJSON('/employee/employment_listing/' + employeeId, {}),
					Grape.fetches.getJSON('/grape-processes/resign_employee_scheduled/active_schedules', {
						process_name: 'resign_employee_scheduled'
					}),
					Grape.fetches.getJSON('/api/record', {
						table: 'user_role', schema: 'grape', limit: 1000000,
						sortfield: 'role_name', sortorder: 'asc',
						filter: [{ value: employee.grape_user_id, op: '=', field: 'user_id' }]
					}),
					this.loadLookups()
				]);

				if (version !== this.loadVersion || this.employee_id() !== employeeId)
					return;

				const activeSchedule = (schedulesResponse.new || []).find(schedule =>
					schedule.param && schedule.param.employee_id == employeeId
				);

				this.pending_resignation(Boolean(activeSchedule));
				this.pending_resignation_schedule_id(activeSchedule ? activeSchedule.schedule_id : null);

				const employmentInfo = (employmentResponse.employment_listing || []).map((item, index) =>
					this.createDepartment({
						...item,
						array_pos: index,
						dates: this.formatEmploymentDates(item)
					})
				);

				this.employment_info(employmentInfo);
				this.bindings.employment_info = employmentInfo;
				this.user_access_roles(rolesResponse.records || []);
				this.updateRoleButtons(rolesResponse.records || []);
			}
			catch (error)
			{
				console.error('Failed to load employment page:', error);
				Grape.alerts.alert({
					type: 'error', title: tr('Error'),
					message: tr('Unable to load employment information.')
				});
			}
			finally
			{
				if (version === this.loadVersion)
					this.loader(false);
				this.loadPromise = null;
				this.loadPromiseEmployeeId = null;
			}
		})();

		return this.loadPromise;
	}

	formatEmploymentDates(item)
	{
		let end = 'Unknown';
		if (item.date_ended)
			end = item.date_ended;
		else if (item.active)
			end = 'Current';
		return `${item.date_started} - ${end}`;
	}

	updateRoleButtons(records)
	{
		if (!Permissions.isLoggedInUser_Admin())
			return;

		const roles = new Set(records.map(record => record.role_name));
		$('#btnApproveUser').toggle(roles.has('unverified_user'));
		$('#btnPromoteManager').toggle(roles.has('manager') && !roles.has('general_manager'));
		$('#btnDemoteManager').toggle(roles.has('general_manager'));
		$('#divPromoteDemoteHelp').toggle(roles.has('manager') || roles.has('general_manager'));
	}

	async loadDepartmentsForRow(row, companyId, resetSelection = false, departmentIdToRestore = null)
	{
		if (!companyId)
		{
			row.departments([]);
			if (resetSelection)
				row.department_id('');
			return;
		}

		const requestedCompanyId = companyId;
		const currentDepartmentId = resetSelection ? null : (departmentIdToRestore ?? row.department_id());
		row.departments_loading(true);

		try
		{
			const response = await Grape.fetches.getJSON('/api/record', {
				table: 'department', schema: 'public', limit: 1000000,
				sortfield: 'department', sortorder: 'asc',
				filter: [{ value: requestedCompanyId, op: '=', field: 'company_id' }]
			});

			if (row.company_id() != requestedCompanyId)
				return;

			const departments = response.records || [];

			row.departments(departments);

			const savedDepartment = departments.find(department =>
				String(department.department_id) === String(currentDepartmentId)
			);

			row.department_id(
				savedDepartment
					? savedDepartment.department_id
					: ''
			);
		}
		catch (error)
		{
			console.error('Failed to load departments:', error);
			Grape.alerts.alert({ type: 'error', title: tr('Error'), message: tr('Unable to load departments.') });
		}
		finally
		{
			if (row.company_id() == requestedCompanyId)
				row.departments_loading(false);
		}
	}

	async updateData()
	{
		if (typeof this.bindings.onReload === 'function')
		{
			await this.bindings.onReload();
			return;
		}
		await this.loadPage();
	}

	delete_employee = async () =>
	{
		const confirmed = await Grape.alerts.confirm({
			type: 'warning', title: tr('Warning'),
			message: tr('Are you sure you want to delete this employee permanently? WARNING: This cannot be undone.')
		});
		if (!confirmed)
			return;

		const response = await Grape.fetches.postJSON('/employee/delete_employee', { employee_id: this.employee_id() });
		if (response.status === 'OK')
		{
			Grape.alerts.alert({ type: 'success', title: tr('Success'), message: tr('Successfully deleted employee.') });
			Grape.navigate('/employee/list/0');
		}
		else if (response.status === 'ERROR')
			Grape.alerts.apiError(response);
		else
			Grape.alerts.alert({ type: 'error', title: tr('Error'), message: tr('Something went wrong...') });
	};

	undo_resign_employee = async () =>
	{
		const confirmed = await Grape.alerts.confirm({
			type: 'warning', title: tr('Warning'), message: tr('Are you sure you want to undo this resignation?')
		});
		if (!confirmed)
			return;

		const response = await Grape.fetches.postJSON('/employee/undo_resign_employee', { employee_id: this.employee_id() });
		if (response.status === 'OK')
		{
			Grape.alerts.alert({ type: 'success', title: tr('Success'), message: tr('Undo successful.') });
			await this.updateData();
		}
		else if (response.status === 'ERROR')
			Grape.alerts.apiError(response);
	};

	undo_scheduled_resign_employee = async () =>
	{
		const confirmed = await Grape.alerts.confirm({
			type: 'warning', title: tr('Warning'), message: tr('Are you sure you want to undo this resignation schedule?')
		});
		if (!confirmed)
			return;

		try
		{
			await Grape.fetches.fetch('/api/bgworker/schedule/' + this.pending_resignation_schedule_id(), { method: 'DELETE' });
			this.pending_resignation(false);
			this.pending_resignation_schedule_id(null);
			Grape.alerts.alert({ type: 'success', title: tr('Success'), message: tr('Undo successful.') });
			await this.updateData();
		}
		catch (error)
		{
			console.error(error);
			Grape.alerts.alert({ type: 'error', title: tr('Error'), message: tr('Error while undoing resignation.') });
		}
	};

	add_department = async () =>
	{
		await this.loadLookups();
		const rowData = this.createDepartment();
		this.employment_info.unshift(rowData);
		const row = $('.employment_block_root > div:first-child');
		row.find('.view').hide();
		row.find('.edit').show();
	};

	edit_department = async (rowData, event) =>
	{
		const savedDepartmentId = rowData.department_id();

		await this.loadDepartmentsForRow(
			rowData,
			rowData.company_id(),
			false,
			savedDepartmentId
		);

		const wrapper = $(event.currentTarget)
			.closest('.employment_block_wrapper');

		wrapper.find('> .view').hide();
		wrapper.find('> .edit').show();
	};

	save_department = async (rowData, event) =>
	{
		const data = ko.toJS(rowData);
		delete data.dates;
		delete data.departments;
		delete data.departments_loading;
		delete data.array_pos;
		data.employee_id = this.employee_id();
		data.date_ended = data.date_ended || null;
		data.date_started = data.date_started || null;

		this.loader(true);
		let response;
		try
		{
			response = await Grape.fetches.postJSON('/employee/departments/edit', data);
		}
		catch (error)
		{
			response = error.responseJSON || error;
		}
		finally
		{
			this.loader(false);
		}

		if (response.status === 'OK')
		{
			await Grape.alerts.alert({ type: 'warning', title: tr('Warning'), message: tr('Please make sure all dates are correct and no unknown entries are left.') });
			await Grape.alerts.alert({ type: 'success', title: tr('Success'), message: tr('Successfully saved employment.') });
			await this.updateData();
			return;
		}

		if (response.status === 'CANT_SET_ALL_FALSE')
			Grape.alerts.alert({ type: 'warning', title: tr('Denied'), message: tr('Not saved - you cannot set all employments to inactive.') });
		else if (response.status === 'CANT_SET_DATE_END_NULL')
			Grape.alerts.alert({ type: 'warning', title: tr('Denied'), message: tr('Not saved - you must enter an end date.') });
		else if (response.status === 'ERROR')
			Grape.alerts.apiError(response);
		else
			Grape.alerts.alert({ type: 'error', title: tr('Error'), message: tr('Something went wrong...') });

		const wrapper = $(event.currentTarget).closest('.employment_block_wrapper');
		wrapper.find('> .view').show();
		wrapper.find('> .edit').hide();
	};

	delete_department = async rowData =>
	{
		const data = ko.toJS(rowData);
		if (!data.employee_department_id)
		{
			this.employment_info.remove(rowData);
			return;
		}

		delete data.dates;
		delete data.departments;
		delete data.departments_loading;
		delete data.array_pos;
		data.employee_id = this.employee_id();
		data.date_ended = data.date_ended || null;
		data.date_started = data.date_started || null;

		let message = tr('Are you sure you want to delete this employment entry?');
		if (data.active)
			message = tr('Please note: You are about to delete an active employment entry. This means the previous employment entry would be made active again. Are you sure you want to proceed?');

		const confirmed = await Grape.alerts.confirm({ type: 'warning', title: tr('Warning'), message });
		if (!confirmed)
			return;

		const response = await Grape.fetches.postJSON('/employee/departments/delete', data);
		if (response.status === 'OK')
		{
			Grape.alerts.alert({ type: 'success', title: tr('Success'), message: tr('Successfully deleted employment.') });
			await this.updateData();
		}
		else if (response.status === 'NOT_ALLOWED')
			Grape.alerts.alert({ type: 'warning', title: tr('Denied'), message: tr('A person cannot have less than one employment assigned.') });
		else if (response.status === 'NOT_ALLOWED_ACTIVE_DEPARTMENT')
			Grape.alerts.alert({ type: 'warning', title: tr('Denied'), message: tr('You cannot delete this employment entry because it is currently being used.') });
		else if (response.status === 'ERROR')
			Grape.alerts.apiError(response);
		else
			Grape.alerts.alert({ type: 'error', title: tr('Error'), message: tr('Something went wrong...') });
	};

	async resign_employee()
	{
		await Grape.dialog.open('EmployeeResign', { employee_id: this.employee_id() });
		await this.updateData();
	}

	show_employment_listing_help = () =>
	{
		Grape.dialog.open('EmploymentListingHelp', { employee_id: this.logged_in_employee_id });
	};
}

export default {
	name: 'employee_view_employment_component',
	viewModel: EmployeeViewEmploymentComponentViewModel,
	module_type: 'ko',
	template,
	template_filename: 'translation/components/employee_view_employment_component'
};
